import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Trash2, Upload, X } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, apiError, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { btnGhost, btnPrimary, cardCls, inputCls, labelCls } from "./shared";

const DEFAULT_SECTIONS = [
  { key: "hero", label: "Hero banner", visible: true },
  { key: "categories", label: "Category grid", visible: true },
  { key: "featured", label: "This week's picks", visible: true },
  { key: "marquee", label: "Editorial marquee", visible: true },
  { key: "story", label: "Our promise strip", visible: true },
  { key: "portfolio", label: "Portfolio highlights", visible: true },
  { key: "dispatch", label: "Dispatch gallery", visible: true },
];

function SortableSection({ s, onToggle, onMove, index, total }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: s.key });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex items-center gap-3 rounded-xl bg-white/70 p-3" data-testid={`section-row-${s.key}`}>
      <span {...attributes} {...listeners} data-testid={`section-drag-${s.key}`} className="cursor-grab touch-none rounded-full p-1.5 hover:bg-secondary"><GripVertical size={14} /></span>
      <span className="flex-1 text-sm">{s.label}</span>
      <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <input type="checkbox" data-testid={`section-visible-${s.key}`} checked={s.visible} onChange={() => onToggle(s.key)} className="accent-primary" /> Show
      </label>
      <button data-testid={`section-up-${s.key}`} onClick={() => onMove(s.key, -1)} disabled={index === 0} aria-label="Move up" className="rounded-full p-1.5 hover:bg-secondary disabled:opacity-30"><ArrowUp size={13} /></button>
      <button data-testid={`section-down-${s.key}`} onClick={() => onMove(s.key, 1)} disabled={index === total - 1} aria-label="Move down" className="rounded-full p-1.5 hover:bg-secondary disabled:opacity-30"><ArrowDown size={13} /></button>
    </div>
  );
}

export default function HomepageTab({ settings, products, reload }) {
  const [form, setForm] = useState(null);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const coverRef = useRef(null);
  const slideRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }));

  useEffect(() => {
    if (!settings) return;
    setForm({ ...settings });
    if (Array.isArray(settings.home_sections) && settings.home_sections.length) {
      const known = settings.home_sections.filter((s) => DEFAULT_SECTIONS.some((d) => d.key === s.key));
      const missing = DEFAULT_SECTIONS.filter((d) => !known.some((s) => s.key === d.key));
      setSections([...known, ...missing]);
    }
  }, [settings]);

  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveTexts = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/site-settings", {
        hero_eyebrow: form.hero_eyebrow || "", hero_line1: form.hero_line1 || "", hero_line2: form.hero_line2 || "",
        hero_subtext: form.hero_subtext || "", marquee_items: form.marquee_items || "", announcement_text: form.announcement_text || "",
        home_sections: sections,
      });
      toast.success("Homepage saved");
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/site-assets", fd, { params: { kind: "cover" } });
      toast.success("Banner image replaced");
      reload();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const uploadSlide = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/site-assets", fd, { params: { kind: "hero" } });
      toast.success("Banner slide added");
      reload();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const removeSlide = async (url) => {
    if (!window.confirm("Remove this banner slide?")) return;
    try {
      await api.put("/admin/site-settings", { hero_slides: (form.hero_slides || []).filter((u) => u !== url) });
      toast.success("Slide removed");
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggleSection = (key) => setSections((s) => s.map((x) => (x.key === key ? { ...x, visible: !x.visible } : x)));
  const moveSection = (key, dir) => setSections((s) => {
    const i = s.findIndex((x) => x.key === key);
    const j = i + dir;
    if (j < 0 || j >= s.length) return s;
    return arrayMove(s, i, j);
  });
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setSections((s) => arrayMove(s, s.findIndex((x) => x.key === active.id), s.findIndex((x) => x.key === over.id)));
  };

  const toggleFlag = async (p, flag) => {
    try {
      await api.put(`/admin/products/${p.id}`, {
        name: p.name, category_id: p.category_id, description: p.description, price: p.price,
        compare_at_price: p.compare_at_price, stock: p.stock, is_new: flag === "is_new" ? !p.is_new : p.is_new,
        is_bestseller: flag === "is_bestseller" ? !p.is_bestseller : p.is_bestseller,
        is_trending: flag === "is_trending" ? !p.is_trending : p.is_trending,
        is_active: p.is_active, variants: p.variants || [], tags: p.tags || [], highlights: p.highlights || [],
      });
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <form onSubmit={saveTexts} className="space-y-6" data-testid="admin-homepage-tab">
      <div className={cardCls}>
        <p className="font-display text-lg">Hero banner</p>
        <p className="mt-1 text-xs font-light text-muted-foreground">Leave a text field empty to keep the current wording.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className={labelCls}>Eyebrow line<input data-testid="hero-eyebrow-input" className={inputCls} value={form.hero_eyebrow || ""} onChange={set("hero_eyebrow")} placeholder="Gifts ✦ Accessories ✦ Jewellery" /></label>
          <label className={labelCls}>Announcement ticker<input data-testid="home-ticker-input" className={inputCls} value={form.announcement_text || ""} onChange={set("announcement_text")} /></label>
          <label className={labelCls}>Headline line 1<input data-testid="hero-line1-input" className={inputCls} value={form.hero_line1 || ""} onChange={set("hero_line1")} placeholder="Thoughtful picks" /></label>
          <label className={labelCls}>Headline line 2<input data-testid="hero-line2-input" className={inputCls} value={form.hero_line2 || ""} onChange={set("hero_line2")} placeholder="for every you" /></label>
          <label className={`${labelCls} sm:col-span-2`}>Intro paragraph<textarea data-testid="hero-subtext-input" rows={2} className={inputCls} value={form.hero_subtext || ""} onChange={set("hero_subtext")} /></label>
          <label className={`${labelCls} sm:col-span-2`}>Marquee words (comma separated)<input data-testid="marquee-items-input" className={inputCls} value={form.marquee_items || ""} onChange={set("marquee_items")} placeholder="Vintage Watches, Gift Hampers, Fine Jewellery" /></label>
        </div>
        <div className="mt-5">
          <p className={labelCls}>Banner images</p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {form.cover_image_url && (
              <span className="relative">
                <img src={imgUrl(form.cover_image_url)} alt="Main banner" className="h-24 w-36 rounded-xl object-cover ring-2 ring-primary" data-testid="home-cover-preview" />
                <span className="absolute -left-1 -top-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[8px] tracking-widest text-white uppercase">Main</span>
              </span>
            )}
            {(form.hero_slides || []).map((u) => (
              <span key={u} className="relative">
                <img src={imgUrl(u)} alt="Banner slide" className="h-24 w-36 rounded-xl object-cover" />
                <button type="button" data-testid="hero-slide-remove" onClick={() => removeSlide(u)} aria-label="Remove slide"
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-1 text-white"><X size={10} /></button>
              </span>
            ))}
            <button type="button" data-testid="cover-replace-btn" onClick={() => coverRef.current?.click()} className={btnGhost}><Upload size={13} /> Replace main</button>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="cover-replace-input" onChange={uploadCover} />
            <button type="button" data-testid="hero-slide-add-btn" onClick={() => slideRef.current?.click()} className={btnGhost}><Upload size={13} /> Add slide</button>
            <input ref={slideRef} type="file" accept="image/*" className="hidden" data-testid="hero-slide-input" onChange={uploadSlide} />
          </div>
          <p className="mt-2 text-[11px] font-light text-muted-foreground">With slides added, the banner gently crossfades between images. With none, only the main banner shows — exactly like now.</p>
        </div>
      </div>

      <div className={cardCls}>
        <p className="font-display text-lg">Homepage sections</p>
        <p className="mt-1 text-xs font-light text-muted-foreground">Drag or use arrows to change the order. Untick Show to hide a section. Press Save below to apply.</p>
        <div className="mt-4 space-y-2" data-testid="sections-list">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={sections.map((s) => s.key)} strategy={verticalListSortingStrategy}>
              {sections.map((s, i) => (
                <SortableSection key={s.key} s={s} index={i} total={sections.length} onToggle={toggleSection} onMove={moveSection} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <div className={cardCls}>
        <p className="font-display text-lg">Featured, new &amp; bestselling products</p>
        <p className="mt-1 text-xs font-light text-muted-foreground">Tick badges for each product — they apply instantly (no Save needed) and control the NEW badges and shop filters.</p>
        <div className="mt-4 max-h-96 space-y-2 overflow-auto pr-1" data-testid="flags-list">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl bg-white/70 p-2.5" data-testid={`flag-row-${p.id}`}>
              {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="h-9 w-7 rounded-lg object-cover" /> : <span className="flex h-9 w-7 items-center justify-center rounded-lg bg-secondary font-display text-xs">A</span>}
              <span className="line-clamp-1 flex-1 text-xs font-light">{p.name}</span>
              {[["is_new", "New"], ["is_bestseller", "Best"], ["is_trending", "Trend"]].map(([flag, l]) => (
                <label key={flag} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <input type="checkbox" data-testid={`flag-${flag}-${p.id}`} checked={!!p[flag]} onChange={() => toggleFlag(p, flag)} className="accent-primary" /> {l}
                </label>
              ))}
            </div>
          ))}
        </div>
      </div>

      <button type="submit" data-testid="homepage-save-btn" className={btnPrimary}>Save homepage</button>
    </form>
  );
}
